import React, {useState, useEffect} from "react";
import {Card} from 'react-bootstrap';
import {Link, useNavigate} from 'react-router-dom';
import {apiClient} from '../api/ApiClient';
import useFastPhotos from '../fastPhotos/useFastPhotos';
import './FavoritesComponent.css';
import Badge from "react-bootstrap/Badge";
import PaginationComponent from "../pagingResults/PaginationComponent";
import { Trash2 } from 'lucide-react';

const FavoritesComponent = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalElements, setTotalElements] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  
  const navigate = useNavigate();
  const {fastPhotos, loading: fastPhotosLoading, error: fastPhotosError} = useFastPhotos();

  // Function to record profile view
  const recordProfileView = async (memberId) => {
    try {
      console.log('Recording view for profile ID:', memberId);
      
      const response = await apiClient.post('/rest/recordView', {
        viewedMemberId: memberId
      });
      
      console.log('View recorded response:', response.data);
    } catch (error) {
      console.error('Error recording profile view:', error);
    }
  };

  // Handle click on a favorite profile
  const handleFavoriteProfileClick = (e, memberId) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (token) {
      // Record the profile view
      recordProfileView(memberId);
      // Navigate to the profile page
      navigate(`/profile/view/1/${memberId}`);
    } else {
      navigate('/login');
    }
  };

  // Handle click on a close match profile
  const handleCloseMatchClick = (e, memberId) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (token) {
      // Record the profile view
      recordProfileView(memberId);
      // Navigate to the profile page
      navigate(`/profile/view/1/${memberId}`);
    } else {
      navigate('/login');
    }
  };

  const handleDeleteFavorite = async (favoriteId) => {
    try {
      const favoriteRequest = {
        favorites: [
          {
            name: "favorite_delete_list",
            val: favoriteId.toString()
          }
        ]
      };
      const response = await apiClient.post('/rest/deleteFavorite', favoriteRequest);
      if (response.status === 200) {
        // Refresh data after deletion
        fetchFavorites();
      }
    } catch (error) {
      console.error('Error deleting favorite:', error);
    }
  };

  const fetchFavorites = async () => {
    setLoading(true);
    try {
      // Use the updated endpoint with pagination parameters
      const response = await apiClient.get(`/rest/favorites?page=${currentPage}&size=${itemsPerPage}`);
      
      if (response.status === 200) {
        console.log('Favorites response:', response.data);
        
        // Handle Spring Boot Page object
        if (response.data.content) {
          // If the response is a Page object
          setFavorites(response.data.content);
          setTotalElements(response.data.totalElements);
        } else {
          // Fallback for backward compatibility
          const validData = response.data.filter(item => Array.isArray(item) && item.length > 1);
          setFavorites(validData);
          setTotalElements(validData.length);
        }
      } else {
        setError('Failed to fetch favorites');
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
      setError('Error fetching favorites. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('Fetching favorites - Page:', currentPage, 'Size:', itemsPerPage);
    fetchFavorites();
  }, [currentPage, itemsPerPage]);

  const formatDateTime = (dateString) => {
    const options = {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    };
    return new Date(dateString).toLocaleString(undefined, options);
  };

  return (
    <div className="container-1024">
      <div className="results-area">
        <h3>My Favorites</h3>
        <div className="results-content">
          <div className="results-center-details">
            <div className="w-100 mb-3">
              <PaginationComponent
                totalItems={totalElements}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(newSize) => {
                  setItemsPerPage(newSize);
                  setCurrentPage(0); // Reset to first page when changing size
                }}
              />
            </div>

            {loading ? (
              <p>Loading favorites...</p>
            ) : error ? (
              <p>{error}</p>
            ) : (
              <div className="card-grid">
                {favorites.length > 0 ? (
                  favorites.map((favorite, index) => (
                    <Card key={index} className="favorite-card position-relative">
                      <div className="delete-icon-wrapper"
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          zIndex: 1,
                          cursor: 'pointer',
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: '50%',
                          padding: '5px'
                        }}>
                        <Trash2
                          size={20}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteFavorite(favorite[0]);
                          }}
                        />
                      </div>
                      {/* Changed from Link to a onClick handler to record view */}
                      <a 
                        href={`/profile/view/1/${favorite[0]}`} 
                        className="favorite-card-link"
                        onClick={(e) => handleFavoriteProfileClick(e, favorite[0])}
                      >
                        <Card.Img 
                          variant="top" 
                          src={`https://pics.netfixup.com/${favorite[21]}/${favorite[20]}`} 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg";
                          }}
                        />
                      </a>
                      <Card.Body>
                        <Card.Title>{favorite[2]}</Card.Title>
                        <Card.Text>
                          <b>Age:</b> {favorite[3]}<br />
                          <b>Gender:</b> {favorite[5]}<br />
                          <b>Looking for:</b> {favorite[6]}<br />
                          <b>Location:</b> {favorite[16]}, {favorite[17]}<br />
                          <b>Online: </b> {favorite[24]}<br />
                          <b>Date Add: </b> {formatDateTime(favorite[23])}<br />
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <p>No Favorites Found</p>
                )}
              </div>
            )}
          </div>

          <div className="results-right-sidebar">
            <h5>Close Matches</h5>
            {fastPhotosLoading ? (
              <p>Loading...</p>
            ) : fastPhotosError ? (
              <p>{fastPhotosError}</p>
            ) : fastPhotos.length > 0 ? (
              fastPhotos.map((photo, index) => (
                <Card key={index} className="fast-photo-card-side">
                  {/* Changed from Link to a onClick handler to record view */}
                  <a 
                    href={`/profile/view/1/${photo[0]}`}
                    onClick={(e) => handleCloseMatchClick(e, photo[0])}
                  >
                    <Card.Img 
                      variant="top" 
                      src={`https://pics.netfixup.com/${photo[7]}${photo[6]}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg";
                      }}
                    />
                  </a>
                  <Badge pill bg="light" text="dark">
                    {photo[2]}
                  </Badge>
                </Card>
              ))
            ) : (
              <p>No Fast Photos Found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FavoritesComponent;